/**
 * Utility functions for working with category trees
 */

/**
 * Filters categories based on a search query
 * @param {Array} categories - The category tree to filter
 * @param {string} query - The search query
 * @param {Set} expandedCategories - Set to track expanded categories
 * @returns {Array} - Filtered category tree
 */
export const filterCategories = (categories, query, expandedCategories) => {
  if (!query.trim()) return [...categories]

  query = query.toLowerCase()
  const tempExpandedCategories = new Set(expandedCategories)

  // Find a category's full path in the tree
  const findCategoryPath = (list, targetId, currentPath = []) => {
    for (const cat of list) {
      const path = [...currentPath, cat.id]
      
      if (cat.id === targetId) {
        return path
      }
      
      if (cat.children && cat.children.length) {
        const result = findCategoryPath(cat.children, targetId, path)
        if (result) return result
      }
    }
    return null
  }

  // Create a deep copy of the categories
  const cloneCategories = (cats) => {
    return cats.map(cat => ({
      ...cat,
      children: cat.children && cat.children.length ? cloneCategories(cat.children) : [],
    }))
  }

  const categoriesCopy = cloneCategories(categories)

  // Recursive search function
  const searchInCategories = (list) => {
    return list.filter(category => {
      const nameMatch = category.name?.toLowerCase().includes(query)
      let childrenMatch = false
      
      if (category.children && category.children.length) {
        const matches = searchInCategories(category.children)
        childrenMatch = matches.length > 0
        
        if (childrenMatch) {
          category.children = matches
          tempExpandedCategories.add(category.id)
        }
      }
      
      if (nameMatch || childrenMatch) {
        // Expand all parent categories in the path
        if (category.level > 1) {
          const path = findCategoryPath(categoriesCopy, category.id)
          if (path) {
            path.forEach(id => tempExpandedCategories.add(id))
          }
        }
        return true
      }
      
      return false
    })
  }

  const results = searchInCategories(categoriesCopy)
  
  // Update the original expandedCategories set
  tempExpandedCategories.forEach(id => expandedCategories.add(id))
  
  return results
}

/**
 * Checks if a category has changes in its children
 * @param {Object} category - The category to check
 * @returns {boolean} - Whether the category has changes in its children
 */
export const hasChangesInChildren = (category) => {
  if (!category.children || !category.children.length) return false
  
  const checkChildrenForChanges = (children) => {
    return children.some(child => {
      if (child.hasPromptChanges) return true
      
      if (child.children && child.children.length) {
        return checkChildrenForChanges(child.children)
      }
      
      return false
    })
  }
  
  return checkChildrenForChanges(category.children)
}

/**
 * Finds a category by ID in a category tree
 * @param {Array} categoryList - The category tree
 * @param {string|number} categoryId - The category ID to find
 * @returns {Object|null} - The found category or null
 */
export const findCategoryById = (categoryList, categoryId) => {
  for (const category of categoryList) {
    if (category.id === categoryId) {
      return category
    }
    
    if (category.children?.length) {
      const found = findCategoryById(category.children, categoryId)
      if (found) return found
    }
  }
  
  return null
}
